import { Editor } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import { TableKit } from '@tiptap/extension-table';
import Image from './tiptap/image';
import TableMenu from './tiptap/table-menu';
import LinkMenu from './tiptap/link-menu';
import { isValidYoutubeUrl } from '@tiptap/extension-youtube';
import { isBilibiliUrl, bilibiliToEmbedUrl } from './tiptap/bilibili';
import { FileHandler } from '@tiptap/extension-file-handler';
import { Markdown } from 'tiptap-markdown';
import {
  CalloutMarkdown,
  HighlightMarkdown,
  YoutubeMarkdown,
  BilibiliMarkdown,
  TableMarkdown,
} from './editor-markdown';

import {
  createUploadFile,
  handleImagePaste,
  handleImageDrop,
  ALLOWED_IMAGE_MIME_TYPES,
} from './tiptap/upload';
import Placeholder from '@tiptap/extension-placeholder';

export default () => {
  let editor;

  return {
    updatedAt: Date.now(), // force Alpine to rerender on selection change

    init() {
      const _this = this;
      const directUploadUrl = this.$root.dataset.directUploadUrl;
      const attachmentsUrl = this.$root.dataset.attachmentsUrl;
      const uploadFile = createUploadFile(directUploadUrl, attachmentsUrl);

      editor = new Editor({
        element: this.$refs.element,
        extensions: [
          StarterKit.configure({
            link: {
              openOnClick: false,
            },
          }),
          LinkMenu,
          CalloutMarkdown,
          Placeholder.configure({
            placeholder: '请输入内容',
          }),
          TableKit.configure({ table: false }),
          TableMarkdown,
          TableMenu,
          (Image as any).configure({
            uploadFile,
          }),
          FileHandler.configure({
            allowedMimeTypes: ALLOWED_IMAGE_MIME_TYPES,
            onPaste: handleImagePaste,
            onDrop: handleImageDrop,
          }),
          HighlightMarkdown,
          YoutubeMarkdown,
          BilibiliMarkdown,
          Markdown.configure({
            html: true,
            tightLists: true,
            bulletListMarker: '-',
            linkify: true,
            transformPastedText: false,
            transformCopiedText: false,
          }),
        ],
        content: this.$refs.target.value,
        onCreate({ editor }) {
          _this.updatedAt = Date.now();
        },
        onUpdate({ editor }) {
          _this.updatedAt = Date.now();
          _this.$refs.target.value = (editor.storage as any).markdown.getMarkdown();
        },
        onSelectionUpdate({ editor }) {
          _this.updatedAt = Date.now();
        },
      });
    },

    destroy() {
      editor.destroy();
    },

    isLoaded() {
      return editor;
    },

    isActive(type, opts = {}) {
      return editor ? editor.isActive(type, opts) : false;
    },

    toggleHeading(opts) {
      editor.chain().toggleHeading(opts).focus().run();
    },

    toggleBold() {
      editor.chain().toggleBold().focus().run();
    },

    toggleItalic() {
      editor.chain().toggleItalic().focus().run();
    },

    toggleStrike() {
      editor.chain().focus().toggleStrike().run();
    },

    toggleBulletList() {
      editor.chain().focus().toggleBulletList().run();
    },

    toggleOrderedList() {
      editor.chain().focus().toggleOrderedList().run();
    },

    toggleCode() {
      editor.chain().focus().toggleCode().run();
    },

    toggleCodeBlock() {
      editor.chain().focus().toggleCodeBlock().run();
    },

    toggleBlockquote() {
      editor.chain().focus().toggleBlockquote().run();
    },

    toggleHighlight() {
      editor.chain().focus().toggleHighlight().run();
    },

    setHorizontalRule() {
      editor.chain().focus().setHorizontalRule().run();
    },

    insertTable() {
      editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
    },

    addLink() {
      editor.chain().setLink({ href: '' }).run();
    },

    addImage() {
      let input = document.createElement('input');
      input.type = 'file';
      input.multiple = true;
      input.accept = 'image/jpeg, image/png, image/gif';

      input.onchange = (event) => {
        Array.from((event.target as HTMLInputElement).files!).forEach((file) => {
          editor.chain().focus().startImageUpload(file).run();
        });
      };

      input.click();
    },

    addVideo() {
      let url = window.prompt('Youtube or Bilibli URL', 'https://');

      if (url === null) return;

      if (isValidYoutubeUrl(url)) {
        editor.chain().focus().setYoutubeVideo({ src: url }).run();
      } else if (isBilibiliUrl(url)) {
        editor
          .chain()
          .focus()
          .setBilibili({ src: bilibiliToEmbedUrl(url) })
          .run();
      } else {
        alert('Invalid URL');
      }
    },

    toggleCallout() {
      editor.chain().focus().toggleCallout({}).run();
    },

    undo() {
      editor.commands.undo();
    },

    redo() {
      editor.commands.redo();
    },
  };
};
